import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import Seo from "../../components/SeoComponent/Seo";
import {
  AddNewsService,
  deleteNews,
  fetchNews,
} from "../../services/newsService/news";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Loader from "../../components/Loader/Loader";
import ReportHeading from "../Components/ReportHeadings/ReportHeadings";
import HeadingTag from "../Components/HeadingComponent/HeadingTag";
import Button from "../Components/ButtonComponent/Button";
import NewsEditModal from "./NewsEditModal";
import moment from "moment/moment";
import { FaEdit } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaEye } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ConfirmPopUp from "../../components/Dialog/ConfirmPopup";

const News = () => {
  const [selectedId, setSelectedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [paginationData, setPaginationData] = useState();
  const [newsData, setNewsData] = useState([]);
  const [isConfirm, setIsConfirm] = useState(false);
  const [formData, setFormData] = useState({
    newsname: "",
  });
  const [errors, setErrors] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleEditClick = (id) => {
    setSelectedId(id);
    setIsModalOpen(true);
  };

  useEffect(() => {
    loadNews();
  }, [currentPage]);

  const loadNews = async (page = currentPage) => {
    setIsLoading(true);
    try {
      const response = await fetchNews(page);
      setNewsData(response?.data);
      setPaginationData(response?.pagination);
      setCurrentPage(response?.pagination?.current_page);
      setLastPage(response?.pagination?.last_page);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openConfirmPopup = (id) => {
    setSelectedId(id);
    setIsConfirm(true); // show confirm modal/dialog
  };

  const handleDeleteClick = async () => {
    if (!selectedId) return;

    try {
      setIsLoading(true);
      const response = await deleteNews(selectedId);
      if (response?.status?.code === 200) {
        setIsConfirm(false);
        showSuccessToast(response.message || "News deleted successfully");
        loadNews();
      } else {
        showErrorToast(response.message || "Failed to delete news");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      showErrorToast("Something went wrong while deleting news");
    } finally {
      setIsLoading(false);
      setSelectedId(null);
    }
  };

  const validate = (values) => {
    let errors = {};
    if (!values.newsname) errors.newsname = "News is required";
    return errors;
  };

  const handleQuillChange = (value) => {
    setFormData({
      ...formData,
      newsname: value,
      selectedScope: value,
      ColorCode: value,
    });
  };

  const handleSubmit = async () => {
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const response = await AddNewsService(
        formData.newsname,
        formData.scope,
        formData.ColorCode,
      );

      if (response?.status.code === 201 || response?.status.code === 200) {
        showSuccessToast(response.status.message);
        loadNews();
        setFormData({ newsname: "", scope: "", ColorCode: "" });
      } else {
        showErrorToast("Failed to Add News.");
      }
    } catch (error) {
      console.error("Error adding News:", error);
      showErrorToast("Failed to Add News.");
    }
  };

  const handleUpdate = () => {
    loadNews(); // Refresh table data
  };

  const modules_quill = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      [
        {
          color: [
            "#ff0000",
            "#00ff00",
            "#0000ff",
            "#000000",
            "#00008b",
            "#00FFFF",
            "#FFFFFF",
            "#800080",
          ],
        },
      ],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "color",
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2 }}
    >
      {isLoading && <Loader />}
      <Seo />
      <ConfirmPopUp
        visible={isConfirm}
        onClose={() => setIsConfirm(false)}
        onConfirm={() => handleDeleteClick()}
        title={"Are You sure want to delete product news?"}
      />

      <div className="mt-5 bg-white p-2">
        <ReportHeading name="News" />
        {/* Form Section */}

        {/* --- News Editor --- */}
        <div className="flex justify-center mt-3 mb-[200px]">
          <ReactQuill
            theme="snow"
            value={formData.newsname}
            onChange={handleQuillChange}
            modules={modules_quill}
            formats={formats}
            placeholder="Enter News ...."
            className="md:w-[60%] w-full h-24"
          />
        </div>

        {/* --- Scope & Color Configuration --- */}
        <div className="flex flex-col items-center mt-16 gap-6 w-full max-w-2xl mx-auto p-6 bg-slate-50/50 rounded-2xl border border-slate-100">
          <div className="flex flex-col md:flex-row items-center gap-4 w-full">
            {/* Scope Dropdown */}
            <div className="relative w-full md:w-3/5">
              <select
                className="w-full appearance-none bg-white border border-slate-200 text-slate-700 py-3 px-4 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer font-medium"
                value={formData.scope}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, scope: e.target.value }))
                }
              >
                <option value="" >
                  Select Display Scope
                </option>
                <option value="login">Login Page</option>
                <option value="dashboard">Dashboard</option>
                <option value="both">Both Platforms</option>
              </select>
              {/* Custom dropdown arrow */}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </div>
            </div>

            {/* Color Picker & Value Display */}
            <div className="flex items-center gap-3 w-full md:w-2/5">
              <input
                type="color"
                className="h-12 w-12 shrink-0 cursor-pointer bg-white border border-slate-200 rounded-xl shadow-sm p-0.5 overflow-hidden [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-none [&::-webkit-color-swatch]:rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                value={formData.ColorCode || "#000000"}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    ColorCode: e.target.value,
                  }))
                }
                title="Choose a color"
              />
              <div className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm text-sm font-mono text-slate-600 text-center uppercase tracking-wider">
                {formData.ColorCode || "Select Color"}
              </div>
            </div>
          </div>

          {/* Quick Color Presets */}
          <div className="flex items-center gap-3 text-sm flex-wrap justify-center w-full pt-2 border-t border-slate-200/60">
            <span className="text-slate-500 font-semibold mr-2 text-xs uppercase tracking-wider">
              Quick Picks:
            </span>

            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, ColorCode: "#FFC107" }))
              }
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-yellow-400 hover:shadow-sm transition-all group"
            >
              <span className="w-3.5 h-3.5 rounded-full bg-[#FFC107] shadow-inner group-hover:scale-110 transition-transform"></span>
              <span className="text-slate-600 font-medium text-xs">Yellow</span>
            </button>

            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, ColorCode: "#F44336" }))
              }
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-red-400 hover:shadow-sm transition-all group"
            >
              <span className="w-3.5 h-3.5 rounded-full bg-[#F44336] shadow-inner group-hover:scale-110 transition-transform"></span>
              <span className="text-slate-600 font-medium text-xs">Red</span>
            </button>

            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, ColorCode: "#4CAF50" }))
              }
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-green-400 hover:shadow-sm transition-all group"
            >
              <span className="w-3.5 h-3.5 rounded-full bg-[#4CAF50] shadow-inner group-hover:scale-110 transition-transform"></span>
              <span className="text-slate-600 font-medium text-xs">Green</span>
            </button>
          </div>
        </div>

        <div className="flex justify-center md:mt-4 mt-24">
          <Button
            className="text-center text-white bg-blue-700 shadow-lg bg-gradient-to-r from-blue-900
                         via-teal-400 to-purple-900 font-semibold p-0 rounded-md px-2 py-1 text-lg hover:bg-red-300"
            type="submit"
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
        {/* Report Section */}
        <HeadingTag
          className="text-2xl text-blue-800 font-bold bg-white text-center mt-2"
          text="Report"
        />
        <div className="overflow-x-auto my-1 p-1">
          <div className="container mx-auto p-1">
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="text-md">
                  <tr className="bg-blue-800 text-white text-center">
                    <th className="border-2 p-2">S No.</th>
                    <th className="border-2">News</th>
                    <th className="border-2">Scope</th>
                    <th className="border-2">Color Code</th>
                    <th className="border-2">Created At</th>
                    <th className="border-2">Updated At</th>
                    <th className="border-2">Action</th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  {newsData?.length > 0 ? (
                    newsData.map((item, index) => (
                      <tr key={(currentPage - 1) * lastPage + index + 1}>
                        <td className="border shadow-inner p-1">
                          {(currentPage - 1) * paginationData?.per_page +
                            index +
                            1}
                        </td>
                        <td className="border shadow-inner p-1">
                          <div
                            dangerouslySetInnerHTML={{ __html: item.news }}
                          />

                          {/* {item?.news} */}
                        </td>
                        <td className="border shadow-inner p-1 bg-lime-100 font-bold">
                          {item?.scope || ""}
                        </td>
                        <td
                          className="border shadow-inner p-1"
                          style={{
                            backgroundColor: item?.colorCode || "transparent",
                          }}
                        >
                          {item?.colorCode || ""}
                        </td>
                        <td className="border shadow-inner p-1">
                          {moment(item?.created_at).format("DD-MM-YYYY")}
                        </td>
                        <td className="border shadow-inner p-1">
                          {moment(item?.updated_at).format("DD-MM-YYYY")}
                        </td>
                        <td className="border shadow-inner p-1 inline-flex gap-3">
                          <div className="flex justify-center">
                            <FaEdit
                              size={20}
                              onClick={() => handleEditClick(item?.id)}
                              className="text-blue-700 hover:bg-white hover:text-green-600"
                            />
                          </div>
                          <div className="flex justify-center">
                            <MdDelete
                              size={20}
                              onClick={() => openConfirmPopup(item?.id)}
                              className="text-blue-700 hover:bg-white hover:text-green-600"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-4 py-2 text-center text-xl">
                        Data Not Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="block lg:hidden">
              {newsData?.length > 0 ? (
                newsData.map((item, index) => (
                  <div
                    key={
                      (currentPage - 1) * (paginationData?.per_page || 0) +
                      index +
                      1
                    }
                    className="border rounded-lg shadow-lg shadow-blue-500 mb-2 p-2 bg-gradient-to-r from-blue-100 via-white to-blue-50 transition-transform transform hover:scale-105"
                  >
                    {/* S No. */}
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold text-gray-700">S No.:</span>
                      <span className="text-blue-700 font-medium">
                        {(currentPage - 1) * (paginationData?.per_page || 0) +
                          index +
                          1}
                      </span>
                      <span className="font-bold text-gray-700">
                        Created At:
                      </span>
                      <span className="text-gray-600">
                        {moment(item?.created_at).format("DD-MM-YYYY")}
                      </span>
                    </div>

                    {/* Updated At */}
                    <div className="flex justify-between items-center">
                      <button
                        onClick={() => handleEditClick(item?.id)}
                        className="flex items-center justify-center ml-3 w-8 h-8 rounded-full bg-blue-700 text-white hover:bg-green-500 transition-all duration-300"
                      >
                        <FaEdit size={24} />
                      </button>
                      <button
                        onClick={() => openConfirmPopup(item?.id)}
                        className="flex items-center justify-center ml-3 w-8 h-8 rounded-full bg-blue-700 text-white hover:bg-red-500 transition-all duration-300"
                      >
                        <MdDelete size={24} />
                      </button>
                      <span className="font-bold text-gray-700 ml-7">
                        Updated At:
                      </span>
                      <span className="text-gray-600">
                        {moment(item?.updated_at).format("DD-MM-YYYY")}
                      </span>
                    </div>

                    {/* News */}
                    <div className="flex justify-center items-center mb-1">
                      <span className="font-bold text-gray-700">News:</span>
                      <span
                        className="text-gray-800"
                        dangerouslySetInnerHTML={{ __html: item.news }}
                      ></span>
                      <FaEye />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-xl text-gray-500">
                  Data Not Found
                </div>
              )}
            </div>
            {/* Mobile ENd */}
          </div>

          {isModalOpen && (
            <NewsEditModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              newsId={selectedId}
              onUpdate={handleUpdate}
            />
          )}
          <div className="gap-2 mt-3">
            <div className="flex justify-center gap-3">
              <button
                className="py-1 text-xl rounded-md hover:bg-blue-800 px-4 bg-blue-500 text-white border-none cursor-pointer"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <button
                className="py-1 text-xl rounded-md hover:bg-blue-800 px-4 bg-blue-900 text-white border-none cursor-pointer"
                onClick={() =>
                  setCurrentPage((prev) =>
                    Math.min(prev + 1, paginationData?.last_page || 1),
                  )
                }
                disabled={currentPage === paginationData?.last_page}
              >
                Next
              </button>
              <div className="flex justify-end">
                <p className="text-[17px] font-bold">
                  Page {currentPage} of {paginationData?.last_page || 1}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default News;
